import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/hooks/useSupabase";
import { showToast } from "@/components/Toast";

interface DashboardMetrics {
  total: number;
  approved: number;
  rejected: number;
  sent: number;
  draft: number;
  viewed: number;
  revision: number;
  totalRevenue: number;
  approvedRevenue: number;
  approvalRate: number;
  validProposals: number;
  expiredProposals: number;
  expiringSoonProposals: number;
  recent: Array<{
    id: string;
    token: string;
    client_name: string;
    client_company: string;
    status: string;
    total_value: number;
    created_at: string;
    expires_at: string | null;
  }>;
}

export default function AdminDashboardPage() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) {
          showToast("Sessão expirada. Faça login novamente.", "warning");
          navigate("/admin/login", { replace: true });
          return;
        }
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) {
          showToast("Sessão inválida. Faça login novamente.", "warning");
          navigate("/admin/login", { replace: true });
          return;
        }
        const res = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/dashboard`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            await supabase.auth.signOut();
            showToast("Sessão expirada. Faça login novamente.", "warning");
            navigate("/admin/login", { replace: true });
            return;
          }
          const errorData = await res.json().catch(() => ({ error: undefined }));
          throw new Error(errorData.error || `Erro ${res.status} ao carregar métricas`);
        }
        const data = await res.json();
        setMetrics(data);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Erro desconhecido";
        setError(msg);
        showToast("Erro ao carregar dashboard: " + msg, "error");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    showToast("Você saiu do sistema.", "info");
    navigate("/admin/login");
  };

  // ... resto do código existente ...
  const statusLabel = (status: string) => {
    const labels: Record<string, string> = {
      draft: "Rascunho",
      sent: "Enviada",
      viewed: "Visualizada",
      approved: "Aprovada",
      rejected: "Recusada",
      revision: "Alteração",
    };
    return labels[status] || status;
  };

  const statusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: "bg-gray-100 text-gray-700",
      sent: "bg-blue-50 text-blue-700",
      viewed: "bg-purple-50 text-purple-700",
      approved: "bg-green-50 text-green-700",
      rejected: "bg-red-50 text-red-700",
      revision: "bg-orange-50 text-orange-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return "Sem expiração";
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const isExpired = (iso: string | null) => {
    if (!iso) return false;
    return new Date(iso).getTime() < Date.now();
  };

  const isExpiringSoon = (iso: string | null) => {
    if (!iso) return false;
    const days = Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= 5;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <h1 className="text-lg font-semibold text-gray-900">Dashboard</h1>
              <nav className="hidden md:flex items-center gap-4 text-sm">
                <Link to="/admin/dashboard" className="text-gray-900 font-medium">
                  Dashboard
                </Link>
                <Link to="/admin/proposals" className="text-gray-500 hover:text-gray-700">
                  Propostas
                </Link>
                <Link to="/admin/library" className="text-gray-500 hover:text-gray-700">
                  Biblioteca
                </Link>
                <Link to="/admin/template" className="text-gray-500 hover:text-gray-700">
                  Template
                </Link>
              </nav>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Sair
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Top row: original metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-5 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500 mb-1">Total de Propostas</p>
            <p className="text-3xl font-bold text-gray-900">{metrics.total}</p>
          </div>
          <div className="bg-white p-5 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500 mb-1">Taxa de Aprovação</p>
            <p className="text-3xl font-bold text-gray-900">{metrics.approvalRate}%</p>
          </div>
          <div className="bg-white p-5 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500 mb-1">Faturamento Total</p>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.totalRevenue)}</p>
          </div>
          <div className="bg-white p-5 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-500 mb-1">Faturamento Aprovado</p>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(metrics.approvedRevenue)}</p>
          </div>
        </div>

        {/* Validity metrics row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white p-5 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-check-line text-green-600 text-lg" />
              <p className="text-sm text-gray-500">Propostas Válidas</p>
            </div>
            <p className="text-3xl font-bold text-green-700">{metrics.validProposals}</p>
          </div>
          <div className="bg-white p-5 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-time-line text-yellow-600 text-lg" />
              <p className="text-sm text-gray-500">Próximas do Vencimento</p>
            </div>
            <p className="text-3xl font-bold text-yellow-700">{metrics.expiringSoonProposals}</p>
          </div>
          <div className="bg-white p-5 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <i className="ri-close-circle-line text-red-600 text-lg" />
              <p className="text-sm text-gray-500">Propostas Vencidas</p>
            </div>
            <p className="text-3xl font-bold text-red-700">{metrics.expiredProposals}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Rascunho</span>
              <span className="text-lg font-semibold text-gray-900">{metrics.draft}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-gray-400 h-2 rounded-full"
                style={{ width: `${metrics.total ? (metrics.draft / metrics.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Enviadas</span>
              <span className="text-lg font-semibold text-gray-900">{metrics.sent}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-blue-400 h-2 rounded-full"
                style={{ width: `${metrics.total ? (metrics.sent / metrics.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Visualizadas</span>
              <span className="text-lg font-semibold text-gray-900">{metrics.viewed}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-purple-400 h-2 rounded-full"
                style={{ width: `${metrics.total ? (metrics.viewed / metrics.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Aprovadas</span>
              <span className="text-lg font-semibold text-gray-900">{metrics.approved}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-green-400 h-2 rounded-full"
                style={{ width: `${metrics.total ? (metrics.approved / metrics.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Recusadas</span>
              <span className="text-lg font-semibold text-gray-900">{metrics.rejected}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-red-400 h-2 rounded-full"
                style={{ width: `${metrics.total ? (metrics.rejected / metrics.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">Alteração Solicitada</span>
              <span className="text-lg font-semibold text-gray-900">{metrics.revision}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className="bg-orange-400 h-2 rounded-full"
                style={{ width: `${metrics.total ? (metrics.revision / metrics.total) * 100 : 0}%` }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Propostas Recentes</h2>
            <Link
              to="/admin/proposals"
              className="text-sm text-amber-600 hover:text-amber-700"
            >
              Ver todas
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-6 py-3 font-medium text-gray-500">Cliente</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500">Empresa</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500">Status</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500">Valor</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500">Vencimento</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500">Ação</th>
                </tr>
              </thead>
              <tbody>
                {metrics.recent.map((proposal) => {
                  const expired = isExpired(proposal.expires_at);
                  const expiring = isExpiringSoon(proposal.expires_at);
                  return (
                    <tr key={proposal.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-6 py-3 text-gray-900">{proposal.client_name}</td>
                      <td className="px-6 py-3 text-gray-500">{proposal.client_company}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusColor(proposal.status)}`}>
                          {statusLabel(proposal.status)}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-gray-900 font-medium">
                        {formatCurrency(proposal.total_value)}
                      </td>
                      <td className="px-6 py-3">
                        <span className={`text-xs font-medium ${expired ? 'text-red-600' : expiring ? 'text-yellow-600' : 'text-green-600'}`}>
                          {formatDate(proposal.expires_at)}
                          {expired && ' (vencida)'}
                          {expiring && ' (em breve)'}
                        </span>
                      </td>
                      <td className="px-6 py-3">
                        <Link
                          to={`/admin/proposals/edit/${proposal.id}`}
                          className="text-sm text-amber-600 hover:text-amber-700"
                        >
                          Editar
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}